import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Brain, Search, Sparkles, ChevronDown, ChevronRight, Wand2, Layers, Cloud } from "lucide-react";
import { API, SCOPE_CONFIG, SOURCE_TYPE_LABEL, verticalLabel } from "./kbConstants";
import SaveToKnowledgeButton from "./SaveToKnowledgeButton";

/**
 * Contextual Knowledge panel for a Project workspace. Shows project-scoped
 * knowledge plus inherited team/org knowledge relevant to the active project.
 * The global explorer is for management; this panel is for consumption during
 * active work.
 */
export default function ProjectKnowledgePanel({ projectId, swarm, vertical = "general" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [projectEntries, setProjectEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [ctxOpen, setCtxOpen] = useState(false);
  const [ctxLoading, setCtxLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const importConnected = async () => {
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/kb/ingest/from-connector-docs`, { project_id: projectId });
      toast[data.created || data.updated ? "success" : "info"](data.message);
      if (data.created || data.updated) loadProject();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const buildContext = async () => {
    if (!query.trim()) return;
    setCtxLoading(true);
    try {
      const { data } = await axios.post(`${API}/kb/assemble-context`, {
        query: query.trim(), project_id: projectId, token_budget: 2000,
      });
      setCtx(data);
      setCtxOpen(true);
    } catch (e) { /* silent */ } finally { setCtxLoading(false); }
  };

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/kb/entries`, {
        params: { scope: "project", scope_id: projectId, limit: 50 },
      });
      setProjectEntries((data.entries || []).map((e) => ({
        ...e, vertical: e.metadata?.vertical, tags: e.metadata?.tags,
      })));
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadProject(); }, [loadProject]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/kb/search`, {
          query: query.trim(), project_id: projectId, limit: 8,
        });
        setResults(data.results || []);
      } catch (e) { /* silent */ }
    }, 350);
    return () => clearTimeout(t);
  }, [query, projectId]);

  return (
    <div className="border border-[var(--qn-border)] rounded-lg bg-[var(--qn-card)]" data-testid="project-knowledge-panel">
      <button
        className="w-full flex items-center justify-between px-4 py-3"
        onClick={() => setExpanded((s) => !s)}
        data-testid="project-knowledge-toggle"
      >
        <span className="flex items-center gap-2 font-semibold text-sm text-[var(--qn-foreground)]">
          <Brain className="w-4 h-4 text-[var(--qn-primary)]" /> Knowledge
          <Badge variant="secondary" className="text-[10px]">{projectEntries.length}</Badge>
        </span>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Relevance search across accessible scopes */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask what the Swarm already knows…"
              className="pl-9 h-9"
              data-testid="project-knowledge-search"
            />
          </div>

          {query.trim() && (
            <div className="space-y-1.5" data-testid="project-knowledge-results">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Relevant across scopes
                </div>
                <button
                  onClick={buildContext}
                  disabled={ctxLoading}
                  className="text-[11px] inline-flex items-center gap-1 text-[var(--qn-primary)] font-medium hover:underline"
                  data-testid="project-knowledge-optimize"
                >
                  {ctxLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Optimize agent context
                </button>
              </div>
              {results.length === 0 ? (
                <div className="text-xs text-slate-400 py-2">No relevant knowledge found.</div>
              ) : results.map((r) => (
                <div key={r.id} className="rounded-md border border-[var(--qn-border)] p-2.5" data-testid={`project-kb-result-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-[var(--qn-foreground)]">{r.title}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${(SCOPE_CONFIG[r.scope] || SCOPE_CONFIG.individual).badge}`}>
                      {(SCOPE_CONFIG[r.scope] || {}).label}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--qn-foreground-muted)] line-clamp-2 mt-0.5">{r.summary}</p>
                  <div className="text-[10px] font-mono text-emerald-600 mt-1">score {r.scores?.final?.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Project-scoped entries */}
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">In this project</div>
            {loading ? (
              <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
            ) : projectEntries.length === 0 ? (
              <div className="text-xs text-slate-400 py-1">No project knowledge yet.</div>
            ) : projectEntries.map((e) => (
              <div key={e.id} className="rounded-md border border-[var(--qn-border)] p-2.5" data-testid={`project-kb-entry-${e.id}`}>
                <div className="text-sm font-medium text-[var(--qn-foreground)]">{e.title}</div>
                <p className="text-xs text-[var(--qn-foreground-muted)] line-clamp-2 mt-0.5">{e.summary}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <Badge variant="outline" className="text-[9px]">{verticalLabel(e.vertical)}</Badge>
                  <Badge variant="outline" className="text-[9px]">{SOURCE_TYPE_LABEL[e.source_type] || e.source_type}</Badge>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <SaveToKnowledgeButton
              content=""
              defaultTitle=""
              projectId={projectId}
              swarm={swarm}
              vertical={vertical}
              label="Add project knowledge"
              variant="outline"
              className="w-full justify-center"
              onSaved={loadProject}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={importConnected}
              disabled={importing}
              className="w-full justify-center gap-1.5 text-[var(--qn-foreground-muted)]"
              data-testid="project-knowledge-import-connected"
            >
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Cloud className="w-3.5 h-3.5" />} Import connected documents
            </Button>
          </div>
        </div>
      )}

      <Dialog open={ctxOpen} onOpenChange={setCtxOpen}>
        <DialogContent className="max-w-2xl" data-testid="project-knowledge-context">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-[var(--qn-primary)]" /> Optimized Agent Context</DialogTitle>
            <DialogDescription>
              The QUBO-optimized "Prior Knowledge" block the Swarm would receive for this query — the best subset under the token budget, with source diversity and de-duplication.
            </DialogDescription>
          </DialogHeader>
          {ctx && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="gap-1"><Layers className="w-3 h-3" /> {ctx.selected}/{ctx.considered} selected</Badge>
                <Badge variant="secondary">{ctx.tokens} / {ctx.token_budget} tokens</Badge>
                <Badge variant="secondary">{ctx.sources} sources</Badge>
                <Badge className="bg-emerald-600">QUBO-optimized</Badge>
              </div>
              <div className="rounded-md border border-[var(--qn-border)] bg-[var(--qn-surface)] p-3 text-sm whitespace-pre-wrap max-h-[55vh] overflow-y-auto font-mono leading-relaxed" data-testid="project-knowledge-context-body">
                {ctx.context || "No prior knowledge selected for this query."}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
